/* eslint-disable react-refresh/only-export-components */
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import type { ReactNode } from "react";
import { convertEntitlementIdsToNames } from "../constants/entitlements";
import { TENANT_CATEGORIES } from "../constants/uiConstants";
import { useChatStore } from "../stores/chatStore";
import { getApiBaseUrl } from "../utils/environment";
import {
  setSentryUser,
  clearSentryUser,
  setSentryTags,
  captureMessage,
  captureException,
} from "../config/sentry";
import {
  persistSession,
  restoreSession,
  clearSession,
  updateSessionUser,
  updateOnboardingInSession,
  onCrossTabAuthEvent,
  getCrossTabSession,
  cleanupLegacyStorage,
  getAuthHeaders,
  getTokenExpiresAt,
  getRefreshToken,
  safeNavigate,
} from "../utils/authStorage";
import { queryClient } from "../providers/QueryProvider";
import { caseChatWebSocketManager } from "../services/caseChatWebSocket";

// API Configuration
const API_BASE_URL = getApiBaseUrl();

/**
 * SECURITY NOTE — Token Storage (GitHub issue #24 — RESOLVED)
 *
 * JWT tokens (access + refresh) are now stored in **httpOnly cookies**
 * set by the backend. They are never accessible to JavaScript, which
 * eliminates the XSS token-theft vector entirely.
 *
 * Non-sensitive session metadata (user profile, entitlement IDs, isAdmin)
 * is kept in **sessionStorage** (per-tab) via `src/utils/authStorage.ts`.
 * This gives each browser tab its own session state, enabling super-admin
 * users to view different tenants side-by-side in multiple tabs.
 *
 * Security controls retained:
 *  1. 401 responses trigger automatic cookie-based refresh → logout flow
 *  2. All session data cleared on logout (including React Query cache)
 *  3. Cross-tab logout broadcast via `StorageEvent` on localStorage
 *  4. Legacy localStorage tokens cleaned up on first load
 */

// Types based on the integration guide
export interface User {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  tenantId: number; // Primary identifier (backend uses tenantId)
  companyId?: number; // Legacy field for backward compatibility
  companyName?: string;
  displayName?: string;
  role?: string;
  tenantCategory?: { id: number; name: string };
  isActive?: boolean;
  entitlements?: Array<{ id: number; name: string; status: string } | string>;
  tenantOnboardingCompleted?: boolean; // Added from login response
}

/** Successful login payload (may omit user/token during MFA challenge). */
export interface LoginResponse {
  token: string | null;
  refreshToken: string | null;
  user: User | null;
  expiresAt: string | null;
  mfaRequired?: boolean;
  mfaChallengeToken?: string;
  mfaEnrollmentPending?: boolean;
  /** Entitlement IDs (same envelope as user; backend sends alongside user). */
  entitlements?: number[];
}

export type LoginOutcome =
  | { kind: "session"; response: LoginResponse }
  | { kind: "mfa_required"; mfaChallengeToken: string }
  | { kind: "mfa_enrollment_pending"; response: LoginResponse };

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<LoginOutcome>;
  completeMfaLogin: (
    mfaChallengeToken: string,
    code: string,
  ) => Promise<LoginResponse>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<boolean>;
  updateOnboardingStatus: (completed: boolean) => void;
  loading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean; // Admin flag for act-as-tenant feature
  isAccountManager: boolean; // True when user's role is "Account Manager"
  hasRole: (role: string) => boolean;
  entitlements: string[]; // Entitlement names from login response
  entitlementIds: number[]; // Entitlement IDs from login response
  hasEntitlement: (entitlementName: string) => boolean; // Quick permission check
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Export the context itself for components that need optional access
export { AuthContext };

/** Fallback context returned when AuthProvider is absent (error boundary scenarios). */
const AUTH_FALLBACK: AuthContextType = {
  user: null,
  login: async () => {
    throw new Error("Auth provider not available");
  },
  completeMfaLogin: async () => {
    throw new Error("Auth provider not available");
  },
  logout: async () => {},
  refreshToken: async () => false,
  updateOnboardingStatus: () => {},
  loading: true,
  isAuthenticated: false,
  isAdmin: false,
  isAccountManager: false,
  hasRole: () => false,
  entitlements: [],
  entitlementIds: [],
  hasEntitlement: () => false,
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    console.error(
      "[useAuth] Called outside AuthProvider. Returning safe fallback. " +
        "This usually means AuthProvider crashed during initialization.",
    );
    captureMessage("useAuth called outside AuthProvider", "warning");
    return AUTH_FALLBACK;
  }
  return context;
};

/**
 * Non-throwing variant of useAuth for components that may render
 * outside AuthProvider (e.g. RootErrorBoundary). Returns null when
 * no AuthProvider is present in the tree.
 */
export const useOptionalAuth = (): AuthContextType | null => {
  return useContext(AuthContext) ?? null;
};

interface AuthProviderProps {
  children: ReactNode;
}

/** Parse API error envelope from login / MFA verify failures. */
function messageFromFailedAuthEnvelope(
  responseData: Record<string, unknown>,
): string {
  let errorMessage = "Request failed";

  if (responseData.errors) {
    const validationErrors: string[] = [];
    for (const [, messages] of Object.entries(responseData.errors)) {
      if (Array.isArray(messages)) {
        validationErrors.push(...messages);
      } else {
        validationErrors.push(messages as string);
      }
    }
    errorMessage = validationErrors.join(". ");
  } else {
    const err = responseData.error as
      | Record<string, unknown>
      | string
      | undefined;
    if (typeof err === "string") {
      errorMessage = err;
    } else if (err && typeof err === "object" && "message" in err) {
      errorMessage = String((err as { message?: string }).message);
    } else if (responseData.message) {
      errorMessage = String(responseData.message);
    }
  }

  return errorMessage;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [entitlementIds, setEntitlementIds] = useState<number[]>([]);
  const [entitlements, setEntitlements] = useState<string[]>([]);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);

  // Timer ref for proactive token refresh
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Proactive refresh scheduler ──────────────────────────────────

  // Refresh buffer: refresh 5 minutes before expiry
  const REFRESH_BUFFER_MS = 5 * 60 * 1000;

  const clearRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  const scheduleProactiveRefresh = useCallback(
    (expiresAt: string | null) => {
      clearRefreshTimer();

      if (!expiresAt) return;

      const expiryMs = new Date(expiresAt).getTime();
      const now = Date.now();
      // Schedule refresh 5 min before expiry, minimum 10 seconds from now
      const delay = Math.max(expiryMs - now - REFRESH_BUFFER_MS, 10_000);

      refreshTimerRef.current = setTimeout(async () => {
        const success = await refreshToken();
        if (!success) {
          // Refresh failed — the reactive 401 flow will handle logout
          // on the next API call, so no action needed here
        }
      }, delay);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clearRefreshTimer],
  );

  // ── Helpers ──────────────────────────────────────────────────────

  /** Populate React state + sessionStorage from a successful auth response */
  const applySessionData = (
    userData: User,
    entitlementIdArray: number[],
    isAdminFlag: boolean,
    tenantOnboardingCompleted: boolean,
    accessToken?: string,
    refreshTokenValue?: string,
    expiresAt?: string,
  ) => {
    const enrichedUser: User = {
      ...userData,
      displayName: `${userData.firstName} ${userData.lastName}`,
      isActive: true,
      tenantOnboardingCompleted,
    };

    setUser(enrichedUser);
    setEntitlementIds(entitlementIdArray);
    setEntitlements(convertEntitlementIdsToNames(entitlementIdArray));
    setIsAdmin(isAdminFlag);

    // Set Sentry user context for error attribution
    setSentryUser({
      id: String(enrichedUser.id),
      email: enrichedUser.email,
      username: enrichedUser.displayName,
    });

    // Set Sentry tags for UX segmentation (tenant, role, viewport)
    setSentryTags({
      tenantId: String(enrichedUser.tenantId),
      role: enrichedUser.role,
      isAdmin: isAdminFlag,
    });

    persistSession({
      user: enrichedUser,
      entitlementIds: entitlementIdArray,
      isAdmin: isAdminFlag,
      tenantOnboardingCompleted,
      accessToken,
      refreshToken: refreshTokenValue,
      expiresAt,
    });

    // Schedule proactive token refresh before expiry
    scheduleProactiveRefresh(expiresAt || null);
  };

  /** Clear all auth state */
  const clearAuthState = () => {
    setUser(null);
    setEntitlementIds([]);
    setEntitlements([]);
    setIsAdmin(false);

    // Clear Sentry user context on logout
    clearSentryUser();
  };

  /** Handle 401 — apiClient already attempted refresh and it failed */
  const handleTokenExpired = async () => {
    await logout();

    // Redirect to login page
    if (typeof window !== "undefined") {
      safeNavigate("/login");
    }
  };

  // ── Login & MFA verify ───────────────────────────────────────────

  const login = async (
    email: string,
    password: string,
  ): Promise<LoginOutcome> => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/user-management/auth/login`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include", // send & receive httpOnly cookies
          body: JSON.stringify({ username: email, password }),
        },
      );

      const responseData = (await response.json()) as Record<string, unknown>;

      if (!response.ok || !responseData.success) {
        throw new Error(messageFromFailedAuthEnvelope(responseData));
      }

      const data = responseData.data as LoginResponse;
      const entitlementIdArray: number[] =
        (responseData.data as { entitlements?: number[] }).entitlements || [];
      const tenantOnboardingCompleted: boolean =
        (responseData.data as { tenantOnboardingCompleted?: boolean })
          .tenantOnboardingCompleted ?? true;
      const isAdminFlag: boolean =
        (responseData.data as { isAdmin?: boolean }).isAdmin ?? false;

      if (data.mfaRequired && data.mfaChallengeToken) {
        return {
          kind: "mfa_required",
          mfaChallengeToken: data.mfaChallengeToken,
        };
      }

      if (data.mfaEnrollmentPending) {
        if (!data.user) {
          throw new Error("Invalid login response");
        }
        applySessionData(
          data.user,
          entitlementIdArray,
          isAdminFlag,
          tenantOnboardingCompleted,
          data.token ?? undefined,
          data.refreshToken ?? undefined,
          data.expiresAt ?? undefined,
        );
        return { kind: "mfa_enrollment_pending", response: data };
      }

      if (!data.user) {
        throw new Error("Invalid login response");
      }

      applySessionData(
        data.user,
        entitlementIdArray,
        isAdminFlag,
        tenantOnboardingCompleted,
        data.token ?? undefined,
        data.refreshToken ?? undefined,
        data.expiresAt ?? undefined,
      );

      return { kind: "session", response: data };
    } catch (error) {
      console.error("Login error:", error);
      throw error;
    }
  };

  const completeMfaLogin = async (
    mfaChallengeToken: string,
    code: string,
  ): Promise<LoginResponse> => {
    const response = await fetch(
      `${API_BASE_URL}/user-management/auth/mfa/verify`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ mfaChallengeToken, code }),
      },
    );

    const responseData = (await response.json()) as Record<string, unknown>;

    if (!response.ok || !responseData.success) {
      throw new Error(messageFromFailedAuthEnvelope(responseData));
    }

    const data = responseData.data as LoginResponse;
    const entitlementIdArray: number[] =
      (responseData.data as { entitlements?: number[] }).entitlements || [];
    const tenantOnboardingCompleted: boolean =
      (responseData.data as { tenantOnboardingCompleted?: boolean })
        .tenantOnboardingCompleted ?? true;
    const isAdminFlag: boolean =
      (responseData.data as { isAdmin?: boolean }).isAdmin ?? false;

    if (!data.user) {
      throw new Error("Invalid MFA response");
    }

    applySessionData(
      data.user,
      entitlementIdArray,
      isAdminFlag,
      tenantOnboardingCompleted,
      data.token ?? undefined,
      data.refreshToken ?? undefined,
      data.expiresAt ?? undefined,
    );

    return data;
  };

  // ── Logout ───────────────────────────────────────────────────────

  const logout = async (): Promise<void> => {
    try {
      // Backend will clear httpOnly cookies on logout
      await fetch(`${API_BASE_URL}/user-management/auth/logout`, {
        method: "POST",
        credentials: "include",
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "application/json",
        },
      });
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      caseChatWebSocketManager.closeAll();
      // Clear sessionStorage + React state
      clearRefreshTimer();
      clearSession();
      clearAuthState();

      // Clear all chat sessions using the store directly
      useChatStore.getState().clearAllSessions();

      // Clear React Query cache to prevent stale data from previous tenant
      queryClient.clear();
    }
  };

  // ── Refresh ──────────────────────────────────────────────────────

  const refreshToken = async (): Promise<boolean> => {
    try {
      // Import the stored refresh token for fallback (non-cookie backends)
      const storedRefreshToken = getRefreshToken();

      // The httpOnly refresh-token cookie is sent automatically via credentials
      // Also send the token in the body as fallback
      const response = await fetch(
        `${API_BASE_URL}/user-management/auth/refresh`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            ...(storedRefreshToken
              ? { Authorization: `Bearer ${storedRefreshToken}` }
              : {}),
          },
          body: JSON.stringify(
            storedRefreshToken ? { refreshToken: storedRefreshToken } : {},
          ),
        },
      );

      // Parse response
      const responseData = await response.json();

      if (!response.ok || !responseData.success) {
        throw new Error(
          responseData.error || responseData.message || "Token refresh failed",
        );
      }

      // Extract data from nested structure
      const data: LoginResponse = responseData.data;
      const entitlementIdArray: number[] = responseData.data.entitlements || [];
      const tenantOnboardingCompleted: boolean =
        responseData.data.tenantOnboardingCompleted ?? true;
      const isAdminFlag: boolean = responseData.data.isAdmin ?? false;

      if (!data.user) {
        return false;
      }

      applySessionData(
        data.user,
        entitlementIdArray,
        isAdminFlag,
        tenantOnboardingCompleted,
        data.token ?? undefined,
        data.refreshToken ?? undefined,
        data.expiresAt ?? undefined,
      );

      return true;
    } catch (error) {
      console.error("Token refresh error:", error);
      // Don't clear session here — let the caller decide whether to log out.
      // This avoids aggressive logout when the refresh endpoint is temporarily
      // unreachable or returns a non-401 error.
      return false;
    }
  };

  // ── Cold-start session check ─────────────────────────────────────

  useEffect(() => {
    const checkAuth = async () => {
      try {
        // One-time cleanup: remove any legacy localStorage tokens
        cleanupLegacyStorage();

        // 1. Try to restore from sessionStorage (covers same-tab refresh)
        const stored = restoreSession();

        if (stored.user) {
          // Prefer cross_tab_session when present — it is rewritten on every
          // login/refresh in any tab, so it carries the freshest entitlements
          // and isAdmin flag. sessionStorage may be stale if entitlements were
          // updated server-side or via login in another tab.
          const crossTab = getCrossTabSession();
          const useCrossTab = crossTab && crossTab.user.id === stored.user.id;
          const entitlementIds = useCrossTab
            ? crossTab.entitlementIds
            : stored.entitlementIds;
          const isAdminFlag = useCrossTab ? crossTab.isAdmin : stored.isAdmin;
          const onboarding = useCrossTab
            ? crossTab.tenantOnboardingCompleted
            : stored.tenantOnboardingCompleted;

          // Restore state from sessionStorage immediately to avoid flash.
          // The auth cookie (httpOnly) is still attached by the browser on
          // every subsequent API request, so there is no need to call a
          // separate /auth/me endpoint here.  If the cookie has expired the
          // first real API call will return 401, which the apiClient error
          // interceptor already handles by triggering logout.
          applySessionData(
            stored.user,
            entitlementIds,
            isAdminFlag,
            onboarding,
          );

          // Restore proactive refresh timer from stored expiry
          const storedExpiresAt = getTokenExpiresAt();
          if (storedExpiresAt) {
            scheduleProactiveRefresh(storedExpiresAt);
          }

          setLoading(false);
          return;
        }

        // 2. New tab with empty sessionStorage — try the localStorage cross-tab session.
        const crossTabSession = getCrossTabSession();
        if (crossTabSession) {
          applySessionData(
            crossTabSession.user,
            crossTabSession.entitlementIds,
            crossTabSession.isAdmin,
            crossTabSession.tenantOnboardingCompleted,
            crossTabSession.accessToken,
            crossTabSession.refreshToken,
            crossTabSession.expiresAt,
          );
          setLoading(false);
          return;
        }

        setLoading(false);
      } catch (error) {
        console.error(
          "[AuthProvider] Cold-start session restore failed:",
          error,
        );
        captureException(error);
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  // ── Cross-tab logout listener ────────────────────────────────────

  useEffect(() => {
    const unsubscribe = onCrossTabAuthEvent((event) => {
      if (event === "logout") {
        caseChatWebSocketManager.closeAll();
        clearSession();
        clearAuthState();
        safeNavigate("/login");
      }

      if (event === "login") {
        const crossTabSession = getCrossTabSession();
        if (!crossTabSession) return;

        // If this tab already has a session for the same user, re-sync only
        // when entitlements / admin flag have actually changed. This handles
        // the case where another tab logged in after the user's permissions
        // were updated server-side.
        const currentSession = restoreSession();
        if (currentSession.user) {
          const sameUser = currentSession.user.id === crossTabSession.user.id;
          const sameEntitlements =
            currentSession.entitlementIds.length ===
              crossTabSession.entitlementIds.length &&
            currentSession.entitlementIds.every((id) =>
              crossTabSession.entitlementIds.includes(id),
            );
          const sameAdmin = currentSession.isAdmin === crossTabSession.isAdmin;
          if (sameUser && sameEntitlements && sameAdmin) return;
        }

        applySessionData(
          crossTabSession.user,
          crossTabSession.entitlementIds,
          crossTabSession.isAdmin,
          crossTabSession.tenantOnboardingCompleted,
          crossTabSession.accessToken,
          crossTabSession.refreshToken,
          crossTabSession.expiresAt,
        );
      }
    });
    return unsubscribe;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── 401 event from API client ────────────────────────────────────

  useEffect(() => {
    const handleAuthLogout = async () => {
      await handleTokenExpired();
    };

    const typedHandler = handleAuthLogout as unknown as EventListener;
    window.addEventListener("auth:logout", typedHandler);

    return () => {
      window.removeEventListener("auth:logout", typedHandler);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Successful silent refresh from API client ────────────────────

  useEffect(() => {
    const handleAuthRefreshed = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      if (!detail) return;

      const data = detail as LoginResponse;
      const entitlementIdArray: number[] =
        ((detail as Record<string, unknown>).entitlements as number[]) || [];
      const tenantOnboardingCompleted: boolean =
        ((detail as Record<string, unknown>)
          .tenantOnboardingCompleted as boolean) ?? true;
      const isAdminFlag: boolean =
        ((detail as Record<string, unknown>).isAdmin as boolean) ?? false;

      if (data.user) {
        applySessionData(
          data.user,
          entitlementIdArray,
          isAdminFlag,
          tenantOnboardingCompleted,
          data.token ?? undefined,
          data.refreshToken ?? undefined,
          data.expiresAt ?? undefined,
        );
      }
    };

    window.addEventListener("auth:refreshed", handleAuthRefreshed);

    return () => {
      window.removeEventListener("auth:refreshed", handleAuthRefreshed);
    };
  }, []);

  // ── Tab visibility: refresh immediately if token is near expiry ──

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible" || !user) return;

      const expiresAt = getTokenExpiresAt();
      if (!expiresAt) return;

      const expiryMs = new Date(expiresAt).getTime();
      const now = Date.now();

      if (now >= expiryMs) {
        // Token already expired — refresh immediately
        refreshToken();
      } else if (expiryMs - now < REFRESH_BUFFER_MS) {
        // Token is within the refresh buffer — refresh now
        refreshToken();
      } else {
        // Re-schedule the timer (it may have been throttled while hidden)
        scheduleProactiveRefresh(expiresAt);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, scheduleProactiveRefresh]);

  // ── Cleanup refresh timer on unmount ─────────────────────────────

  useEffect(() => {
    return () => clearRefreshTimer();
  }, [clearRefreshTimer]);

  // ── Role & entitlement helpers ───────────────────────────────────

  const hasRole = (role: string): boolean => {
    if (!user || !user.role) return false;

    const userRole = user.role.toLowerCase();
    const checkRole = role.toLowerCase();

    const roleHierarchy: Record<string, string[]> = {
      superadmin: ["superadmin"],
      admin: ["admin", "superadmin"],
      manager: ["manager", "admin", "superadmin"],
      user: ["user", "manager", "admin", "superadmin"],
    };

    return roleHierarchy[checkRole]?.includes(userRole) || false;
  };

  const hasEntitlement = (entitlementName: string): boolean => {
    if (user?.role?.toLowerCase() === "superadmin") {
      return true;
    }
    return entitlements.includes(entitlementName);
  };

  // Update onboarding status after completion
  const updateOnboardingStatus = (completed: boolean) => {
    if (!user) return;

    const updatedUser: User = {
      ...user,
      tenantOnboardingCompleted: completed,
    };

    setUser(updatedUser);
    updateSessionUser(updatedUser);
    updateOnboardingInSession(completed);
  };

  // ── Context value ────────────────────────────────────────────────

  const value: AuthContextType = {
    user,
    login,
    completeMfaLogin,
    logout,
    refreshToken,
    updateOnboardingStatus,
    loading,
    isAuthenticated: !!user,
    isAdmin,
    isAccountManager:
      user?.tenantCategory?.name === TENANT_CATEGORIES.ACCOUNT_MANAGER,
    hasRole,
    entitlements,
    entitlementIds,
    hasEntitlement,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
